import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { CartService } from '../services/cart.service';
import { ComboValidationService } from '../services/combo-validation.service';
import { ApiService } from '../services/api.service';
import { DataService } from '../services/data.service';
import { OrderService, RejectedItemsErrorResponse } from '../services/order.service';
import { ApplePayService } from '../services/apple-pay.service';
import { GooglePayService } from '../services/google-pay.service';
import { OnlinePaymentService } from '../services/online-payment.service';
import { PaymentEventsService } from '../services/payment-events.service';
import { TapRedirectPaymentsService } from '../services/tap-knet-payments.service';
import { ToastService } from '../services/toast.service';
import { MenuService } from '../menu/menu.service';
import {
  CartConfig, Fee, GetOrder, Order, OrderSubmissionOptions, PaymentMethod, ResolvedOrderMetadata,
} from '../models/cart.model';
import { AppliedPromo, AppliedVoucher, VoucherPayload } from '../models/voucher.model';
import { PayNowAtTableResult } from '../models/pay-at-table.model';
import {
  cartSubtotal, createOrder, filterAvailablePaymentMethods, idsForRejectedItems,
  idsForRejectedModifiers, isOrderFormRequired, pickupFieldsFor, toCartLineView, toOrderItems, toPayNowAtTableRequest,
} from './cart.utils';
import { OrderFormComponent, OrderFormResult } from './order-form/order-form.component';
import { PayNowAtTableComponent } from './pay-now-at-table/pay-now-at-table.component';
import { AlertDialogComponent } from '../shared/alert-dialog/alert-dialog.component';
import { currencySymbol } from '../shared/currency.utils';
import { TipSelection } from '../shared/tipping-form/tipping-form.component';
import { OrderStatus, PaymentMode, VoucherMode } from '../shared/enums';

// Owns everything downstream of "user wants to pay": order-form resolution, prepaid
// checkout pricing (voucher/fees), submission, and the Apple/Google Pay/KNET/Tap
// redirect flows. CartComponent stays focused on editing cart contents.
@Injectable({
  providedIn: 'root',
})
export class CartCheckoutService {
  private readonly cartService = inject(CartService);
  private readonly comboValidation = inject(ComboValidationService);
  private readonly apiService = inject(ApiService);
  private readonly dataService = inject(DataService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly toastService = inject(ToastService);
  private readonly orderService = inject(OrderService);
  private readonly applePayService = inject(ApplePayService);
  private readonly googlePayService = inject(GooglePayService);
  private readonly onlinePaymentService = inject(OnlinePaymentService);
  private readonly paymentEventsService = inject(PaymentEventsService);
  private readonly tapRedirect = inject(TapRedirectPaymentsService);
  private readonly menuService = inject(MenuService);

  private readonly cartConfig = signal<CartConfig | null>(null);
  private readonly orderFormData = signal<Record<string, unknown> | null>(null);
  private readonly finalTotal = signal(0);

  private readonly voucherSignal = signal<string | null>(null);
  readonly voucher = this.voucherSignal.asReadonly();

  // Set once a submission has created an order, so a cancelled/failed payment
  // can tell the backend to release it.
  private orderId?: string;

  constructor() {
    this.paymentEventsService.paymentCancelled.subscribe(cancelled => {
      if (!cancelled) return;

      this.submitted.set(false);
      this.isLoading.set(false);
      if (this.orderId) {
        this.apiService.cancelOrder(this.orderId).subscribe();
        this.orderId = undefined;
      }
    });

    // A tip belongs to one basket: it survives leaving and re-entering the cart, but
    // every successful order (and a manual clear or inactivity expiry) empties the cart,
    // so the next order starts without one.
    effect(() => {
      if (this.cartService.cartLines().length > 0) return;
      untracked(() => {
        this.tipAmount.set(0);
        this.tipSelection.set(null);
      });
    });
  }

  readonly voucherInput = signal<string | null>(null);
  readonly hideVoucher = signal(false);
  readonly appliedVoucher = signal<AppliedVoucher | null>(null);
  // An automatic section promotion. Separate from appliedVoucher because both can
  // apply to one basket, on disjoint items, and each has to show its own line -
  // the order's discount_amount is the two added together.
  readonly appliedPromo = signal<AppliedPromo | null>(null);
  readonly fees = signal<Fee[]>([]);
  readonly tipAmount = signal(0);
  readonly tipSelection = signal<TipSelection | null>(null);
  readonly paymentTypes = signal<PaymentMethod[]>([]);

  // Starts disabled until the cart config finishes loading, so a submit can't
  // race past a not-yet-known order-form requirement.
  readonly submitted = signal(true);
  readonly isLoading = signal(false);

  private readonly lines = computed(() => this.cartService.cartLines().map(toCartLineView));
  private readonly subtotal = computed(() => cartSubtotal(this.lines()));
  private readonly isPrepaid = computed(() => this.dataService.restaurant().payment_mode == PaymentMode.Prepaid);
  private readonly isPayAtTable = computed(() => this.dataService.restaurant().payment_mode == PaymentMode.PayAtTable);

  readonly formRequired = computed(() =>
    isOrderFormRequired(this.cartConfig()?.form, this.subtotal()));

  readonly minOrder = computed(() => this.cartConfig()?.min_order ?? 0);
  readonly totalWithTip = computed(() => this.finalTotal() + this.tipAmount());
  readonly isOrderFree = computed(() => this.totalWithTip() <= 0);

  init(): void {
    // The service outlives the cart page, so answers from a previous visit would otherwise
    // be silently resubmitted. Within one visit they're kept, so a retried payment doesn't re-ask.
    this.orderFormData.set(null);

    const restaurantId = this.dataService.restaurant().id;
    this.apiService.getCartConfig(restaurantId).subscribe(config => {
      this.cartConfig.set(config);
      this.submitted.set(false);
    });

    if (this.isPrepaid()) {
      const savedVoucher = this.dataService.voucherName();
      this.voucherInput.set(savedVoucher);
      this.voucherSignal.set(savedVoucher);
    }

    this.setupPaymentMethods();
  }

  onVoucherApplied(data: { voucher: string | null }): void {
    this.voucherSignal.set(data.voucher);
  }

  onTipApplied(data: { tip_amount: number; selection: TipSelection | null }): void {
    this.tipAmount.set(data.tip_amount);
    this.tipSelection.set(data.selection);
  }

  // Prepaid checkout (voucher discount, fees, final total) is server-computed and
  // must be refreshed whenever the cart contents or applied voucher change.
  applyCheckout(voucherName: string | null): void {
    if (this.lines().length === 0) return;

    const payload: VoucherPayload = {
      user_id: this.dataService.getUserId(),
      order_items: toOrderItems(this.cartService.cartLines()),
      table_id: this.dataService.getTableId()!,
      voucher_name: voucherName || undefined,
    };

    this.apiService.applyPrepaidCheckout(this.dataService.restaurant().id, payload).subscribe({
      next: res => {
        const appliedVoucher = Object.keys(res.voucher ?? {}).length > 0 ? res.voucher : null;
        this.hideVoucher.set(appliedVoucher != null);
        this.appliedVoucher.set(appliedVoucher);
        this.appliedPromo.set(res.promo?.amount ? res.promo : null);
        this.fees.set(res.orders[0].fees ?? []);
        this.finalTotal.set(res.orders[0].total_price);
        this.dataService.voucherName.set(appliedVoucher?.mode === VoucherMode.Balance ? voucherName : null);
      },
      error: error => {
        this.toastService.error(this.translateService.instant(error.error.message));
        this.hideVoucher.set(false);
        this.appliedVoucher.set(null);
        this.appliedPromo.set(null);
        this.dataService.voucherName.set(null);
        if (voucherName) this.applyCheckout(null);
      },
    });
  }

  async submit(orderNote: string): Promise<void> {
    if (this.blockedByCombo()) return;

    const resolved = await this.resolveOrderMetadata();
    if (!resolved) return;

    await this.submitAndHandleResult(this.buildOrder(orderNote, { metadata: resolved.metadata }));
  }

  async onPrepaidPaymentSelected(method: PaymentMethod, orderNote: string): Promise<void> {
    if (this.blockedByCombo()) return;

    if (this.subtotal() < this.minOrder()) {
      this.toastService.warning(this.translateService.instant('COMMON.MIN_ORDER_ERROR', {
        currency: currencySymbol(this.dataService.restaurant().currency), amount: this.minOrder(),
      }));
      return;
    }

    const resolved = await this.resolveOrderMetadata();
    if (!resolved) return;

    await this.processPaymentSelection(method, orderNote, resolved.metadata);
  }

  displayCheckout(orderNote: string): void {
    if (this.blockedByCombo()) return;

    this.bottomSheet.open(PayNowAtTableComponent, {
      autoFocus: false,
      data: {
        paymentTypes: this.paymentTypes(),
        onPayNowAtTable: (result: PayNowAtTableResult, method?: PaymentMethod) =>
          this.handlePayNowAtTable(result, orderNote, method),
      },
    });
  }

  private async handlePayNowAtTable(
    result: PayNowAtTableResult, orderNote: string, method?: PaymentMethod,
  ): Promise<void> {
    this.finalTotal.set(result.firstOrder?.total_price ?? 0);
    this.tipAmount.set(result.tipAmount);

    // No loader until the order form is answered: the cart's overlay sits above the
    // CDK overlay container, so it would cover the form sheet and block it.
    const resolved = await this.resolveOrderMetadata();
    if (!resolved) return;

    const orderOptions: OrderSubmissionOptions = { payNow: true, voucherName: result.voucherName };

    if (result.isOrderFree) {
      await this.submitAndHandleResult(this.buildOrder(orderNote, { metadata: resolved.metadata, ...orderOptions }));
      return;
    }

    await this.processPaymentSelection(method!, orderNote, resolved.metadata, orderOptions);
  }

  private async setupPaymentMethods(): Promise<void> {
    const methods = this.dataService.paymentMethods();
    if (!(methods?.length > 0)) return;

    // Skip the Google Pay availability check entirely once an ezetap terminal is
    // active — filterAvailablePaymentMethods() ignores it in that case anyway.
    const ezetapMachineId = this.dataService.ezetapMachineId();
    const googlePayAvailable = ezetapMachineId ? false : await this.googlePayService.isGooglePayAvailable();

    this.paymentTypes.set(filterAvailablePaymentMethods(methods, {
      ezetapMachineId,
      applePayAvailable: this.applePayService.isApplePayAvailable(),
      googlePayAvailable,
    }));
  }

  // The one hard gate for combo rules: nothing blocks editing the cart, only
  // actually committing to an order. Points at the guide block rather than
  // repeating one of its (possibly several) messages here.
  private blockedByCombo(): boolean {
    if (this.comboValidation.isAllValid()) return false;

    this.toastService.error(this.comboValidation.checkoutBlockedMessage());
    return true;
  }

  // Resolves from the Confirm tap itself rather than afterDismissed (which fires on
  // animationend): Safari only allows an ApplePaySession inside a user gesture.
  private openOrderForm(): Promise<OrderFormResult | undefined> {
    const fields = pickupFieldsFor(this.cartConfig()!.form!, this.translateService.currentLang);

    return new Promise(resolve => {
      this.bottomSheet.open(OrderFormComponent, {
        autoFocus: false,
        data: { fields, onConfirm: resolve },
      }).afterDismissed().subscribe(result => resolve(result)); // no-op once onConfirm has resolved
    });
  }

  // Ensures the pickup/order form has been answered when the restaurant requires one,
  // opening it on demand. Returns null when the user dismisses it without answering.
  private async resolveOrderMetadata(): Promise<ResolvedOrderMetadata | null> {
    if (!this.formRequired() || this.orderFormData()) {
      return { metadata: this.orderFormData() ?? undefined };
    }

    const result = await this.openOrderForm();
    if (!result) return null;

    this.orderFormData.set(result.metadata);
    return { metadata: result.metadata };
  }

  private buildOrder(
    orderNote: string,
    options: { metadata?: Record<string, unknown>; paymentType?: string } & OrderSubmissionOptions = {},
  ): Order {
    return createOrder({
      lines: this.cartService.cartLines(),
      userId: this.dataService.getUserId(),
      comment: orderNote,
      tipAmount: this.tipAmount(),
      voucher: this.voucher(),
      isPayAtTable: this.isPayAtTable(),
      ...options,
    });
  }

  private async submitAndHandleResult(order: Order): Promise<void> {
    this.submitted.set(true);
    this.isLoading.set(true);

    const tableId = this.dataService.getTableId()!;
    const result = await this.orderService.submitOrder(tableId, order);

    if (result.order) {
      this.isLoading.set(false);
      this.cartService.clearCart();
      this.orderService.setLastOrder(result.order);
      return;
    }

    this.handleSubmitFailure(result);
  }

  private async processPaymentSelection(
    method: PaymentMethod,
    orderNote: string,
    metadata?: Record<string, unknown>,
    orderOptions: OrderSubmissionOptions = {},
  ): Promise<void> {
    this.submitted.set(true);
    this.isLoading.set(true);

    const order = this.buildOrder(orderNote, { metadata, paymentType: method.key, ...orderOptions });

    if (method.key === 'applepay') {
      this.submitApplePayOrder(order);
      return;
    }

    if (method.key === 'googlepay') {
      await this.submitGooglePayOrder(order);
      return;
    }

    const tableId = this.dataService.getTableId()!;
    const result = await this.orderService.submitOrder(tableId, order);

    if (!result.order) {
      this.handleSubmitFailure(result);
      return;
    }

    const orderId = result.order.order_id;
    this.orderId = orderId;

    if (method.key === 'knet' || method.key === 'tap') {
      this.tapRedirect.startRedirect([orderId], method.key === 'knet' ? 'knet' : 'all');
      return; // loader intentionally stays on — page will navigate away
    }

    if (method.is_online) {
      const payNowRequest = order.pay_now ? toPayNowAtTableRequest(result.order) : null;
      await this.onlinePaymentService.startPayment([orderId], payNowRequest, order.tip_amount ?? 0);
      return; // loader stays on — the paymentCancelled subscription handles the false case
    }

    // Offline payments are confirmed immediately.
    this.isLoading.set(false);
    this.cartService.clearCart();
    this.orderService.setLastOrder(result.order);
  }

  private submitApplePayOrder(order: Order): void {
    let applePaySession;
    try {
      applePaySession = this.applePayService.createApplePaySession(
        this.dataService.restaurant().currency, this.totalWithTip(),
      );
    } catch {
      // Throws outside a user gesture, e.g. after the order form's room-check network
      // call. The form answers are kept, so the next tap goes straight to Apple Pay.
      this.submitted.set(false);
      this.isLoading.set(false);
      this.toastService.error(this.translateService.instant('PAYMENT.APPLE_PAY_TAP_AGAIN'));
      return;
    }
    const tableId = this.dataService.getTableId()!;
    let placedOrder: GetOrder | undefined;

    this.orderService.submitOrder(tableId, order).then(result => {
      if (result.order) {
        placedOrder = result.order;
        const orderId = result.order.order_id;
        this.orderId = orderId;
        const payNowRequest = order.pay_now ? toPayNowAtTableRequest(result.order) : null;
        this.applePayService.startApplePaySession(applePaySession, [orderId], payNowRequest, order.tip_amount ?? 0);

        applePaySession.oncancel = () => {
          this.submitted.set(false);
          this.isLoading.set(false);
          if (this.orderId) this.apiService.cancelOrder(this.orderId).subscribe();
        };
        return;
      }

      this.handleSubmitFailure(result);
    });

    this.applePayService.ApplePaySuccess.pipe(take(1)).subscribe(success => {
      if (success && placedOrder) {
        placedOrder.status = OrderStatus.Paid;
        this.isLoading.set(false);
        this.cartService.clearCart();
        this.orderService.setLastOrder(placedOrder);
        return;
      }

      this.isLoading.set(false);
      this.submitted.set(false);
      this.toastService.error(this.translateService.instant('PAYMENT.ERROR'));
      if (this.orderId) this.apiService.cancelOrder(this.orderId).subscribe();
    });
  }

  private async submitGooglePayOrder(order: Order): Promise<void> {
    const tableId = this.dataService.getTableId()!;
    const result = await this.orderService.submitOrder(tableId, order);

    if (!result.order) {
      this.handleSubmitFailure(result);
      return;
    }

    const placedOrder: GetOrder = result.order;
    const orderId = placedOrder.order_id;
    this.orderId = orderId;
    const totalStr = ((placedOrder.total_price || 0) + (Number(placedOrder.tip_amount) || 0)).toFixed(2);

    this.googlePayService.GooglePaySuccess.pipe(take(1)).subscribe(success => {
      this.isLoading.set(false);
      if (success) {
        placedOrder.status = OrderStatus.Paid;
        this.cartService.clearCart();
        this.orderService.setLastOrder(placedOrder);
        return;
      }

      this.submitted.set(false);
      if (this.orderId) this.apiService.cancelOrder(this.orderId).subscribe();
    });

    try {
      const payNowRequest = order.pay_now ? toPayNowAtTableRequest(placedOrder) : null;
      await this.googlePayService.startGooglePaySession(
        [orderId], totalStr, placedOrder.currency, payNowRequest, order.tip_amount ?? 0,
      );
    } catch {
      this.isLoading.set(false);
      this.submitted.set(false);
      this.toastService.error(this.translateService.instant('PAYMENT.ERROR'));
      if (this.orderId) this.apiService.cancelOrder(this.orderId).subscribe();
    }
  }

  private handleSubmitFailure(result: { menuClosed?: boolean; error?: RejectedItemsErrorResponse }): void {
    this.isLoading.set(false);
    this.submitted.set(false);
    if (result.menuClosed) return;
    if (result.error?.modifiers || result.error?.items) {
      this.handleRejectedItems(result.error);
      return;
    }

    if (result.error?.message) {
      this.toastService.error(this.translateService.instant(result.error.message));
    }
  }

  private handleRejectedItems(response: RejectedItemsErrorResponse): void {
    const lines = this.cartService.cartLines();
    if (response.items?.length > 0) {
      idsForRejectedItems(lines, response.items).forEach(id => this.cartService.removeLine(id));
    }
    if (response.modifiers?.length > 0) {
      idsForRejectedModifiers(lines, response.modifiers).forEach(id => this.cartService.removeLine(id));
    }

    // The rejection means our menu is stale (a menu.updated was likely missed) - refetch it
    // so the menu pages stop offering what was just rejected.
    this.menuService.refresh();

    const names = [...(response.items ?? []), ...(response.modifiers ?? [])].map(item => `*${item.name}`).join(', ');
    const message = names
      ? `${this.translateService.instant('ORDER.REJECTED')} - ${names}`
      : this.translateService.instant('ORDER.REJECTED');

    AlertDialogComponent.open(this.dialog, {
      type: 'warning',
      title: 'CART.OOPS',
      message,
      enableCancelBtn: false,
    }).afterClosed().subscribe(() => this.submitted.set(false));
  }
}
